import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyTypography, BaseButton, useCopyToast, useT } from '@repo/common';
import { ReactComponent as NavigationLeftIcon20 } from '@repo/common/assets/icons/20px/icon_20px_navigation-left.svg';
import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/24px/icon_24px_copy.svg';
import { ReactComponent as CheckCircleIcon } from '@repo/common/assets/icons/32px/icon_32px_check-circle_filled.svg';
import StepIndicator from '../../components/Kpathport/components/StepIndicator';
import {
  formatDate,
  formatTime,
} from '../../components/Kpathport/utils/dateTimeUtils';
import { formatCurrencyAmount } from '../../components/Kpathport/utils/priceUtils';
import { getBankTransferInstruction } from '../../api/payment/payment.api';

const SUPPORT_EMAIL = 'hello@ko-ready.com';

const pad = (n) => String(n).padStart(2, '0');

const formatCountdown = (seconds) => {
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

const secondsUntil = (iso) => {
  if (!iso) return 0;
  const diff = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
  return diff > 0 ? diff : 0;
};

/**
 * 계좌이체 안내 페이지 (계좌이체 워크숍 전용 — 예: Prana Club 한옥 웰니스).
 *
 * 결제 단계에서 Pay now 를 누른 직후, 그리고 마이페이지 "View transfer details" 로 다시 들어올 때 보인다.
 * 첫 카드는 요청서 문구를 그대로 따른다:
 *   Bank transfer details / Account number / Bank / Account holder /
 *   3시간 내 입금·자동 취소 안내 / 환불 정책은 이벤트 상세 페이지 참고.
 * 결제 상태와 예약 상태를 합친 pageState(awaiting / paid / expired / canceled / refund_requested / refunded)에
 * 따라 머리말·버튼이 바뀐다. 계좌 정보는 입금 대기 중에만 보인다.
 */
const BankTransfer = () => {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { copy, copyToast } = useCopyToast();

  const [instruction, setInstruction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);

  const orderId = searchParams.get('orderId');

  useEffect(() => {
    const load = async () => {
      if (!orderId) {
        setError(t('Booking information is missing.', '예약 정보가 없습니다.'));
        setLoading(false);
        return;
      }
      try {
        const data = await getBankTransferInstruction(orderId);
        setInstruction(data);
        setSecondsLeft(secondsUntil(data?.deadlineAt));
      } catch (err) {
        console.error('Failed to load bank transfer instruction:', err);
        setError(
          err?.response?.data?.msg ||
            err?.response?.data?.message ||
            t(
              'Could not load your transfer details.',
              '입금 안내를 불러오지 못했습니다.',
            ),
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orderId, t]);

  const status = String(instruction?.paymentStatus || '').toUpperCase();
  const itemStatuses = (instruction?.items || []).map((item) =>
    String(item.bookingStatus || '').toUpperCase(),
  );
  const allClosed =
    itemStatuses.length > 0 &&
    itemStatuses.every((s) => ['CANCELED', 'EXPIRED', 'REFUNDED'].includes(s));
  const anyRefunded = itemStatuses.includes('REFUNDED');
  // 마감이 지났는데 스케줄러가 아직 EXPIRED 로 돌리지 않은 짧은 구간도 "만료" 로 보여 준다
  const deadlinePassed = Boolean(instruction?.deadlineAt) && secondsLeft <= 0;

  // 결제 상태와 예약 상태를 합쳐 페이지 상태 하나로 만든다.
  //  paid              입금 확인됨 (PARTIAL_CANCELED 는 일부 환불 뒤에도 남은 예약이 있는 상태)
  //  refund_requested  입금 뒤 환불을 요청해 예약은 REFUNDED, 환불 송금은 아직 (결제는 DONE 그대로)
  //  refunded          환불 완료 (결제 CANCELED + 예약 REFUNDED)
  //  canceled          입금 전 취소, 또는 입금 뒤 관리자 취소
  //  expired           입금 마감 초과 — 관리자가 입금을 확인하면 자리가 남아 있을 때 다시 확정될 수 있다
  //  awaiting          입금 대기
  const pageState = (() => {
    if (status === 'DONE' || status === 'PARTIAL_CANCELED') {
      if (allClosed) return anyRefunded ? 'refund_requested' : 'canceled';
      return 'paid';
    }
    if (anyRefunded && allClosed) return 'refunded';
    if (status === 'EXPIRED') return 'expired';
    if (status === 'CANCELED' || status === 'ABORTED') return 'canceled';
    if (allClosed) {
      return itemStatuses.every((s) => s === 'EXPIRED') ? 'expired' : 'canceled';
    }
    if (deadlinePassed) return 'expired';
    return 'awaiting';
  })();
  const isPaid = pageState === 'paid';
  const isAwaiting = pageState === 'awaiting' && Boolean(instruction);

  // 예약 줄마다 붙는 상태 표기. REFUNDED 는 환불 송금 전(결제 DONE)이면 "요청됨" 으로 구분한다
  const itemStatusLabel = (bookingStatus) => {
    const s = String(bookingStatus || '').toUpperCase();
    if (s === 'REFUNDED') {
      return status === 'DONE'
        ? t('Refund requested', '환불 요청됨')
        : t('Refunded', '환불됨');
    }
    return (
      {
        HOLD: t('Awaiting transfer', '입금 대기'),
        OFFERED: t('Awaiting transfer', '입금 대기'),
        CONFIRMED: t('Confirmed', '확정'),
        ATTENDED: t('Confirmed', '확정'),
        ABSENT: t('Confirmed', '확정'),
        CANCELED: t('Canceled', '취소됨'),
        EXPIRED: t('Expired', '만료됨'),
      }[s] || ''
    );
  };

  useEffect(() => {
    if (!isAwaiting || secondsLeft <= 0) return undefined;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [isAwaiting, secondsLeft]);

  const amountText = useMemo(
    () =>
      instruction
        ? formatCurrencyAmount(instruction.amount, instruction.currency)
        : '',
    [instruction],
  );

  const deadlineText = useMemo(() => {
    if (!instruction?.deadlineAt) return '';
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Seoul',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(new Date(instruction.deadlineAt));
    } catch {
      return '';
    }
  }, [instruction]);

  const firstItem = instruction?.items?.[0];
  const eventDetailsPath = firstItem?.workshopId
    ? `/community/kpathport/workshop/${firstItem.workshopId}`
    : '/community/kpathport/pathfeed';

  // 요청서 순서 그대로: Account number → Bank → Account holder
  const detailRows = instruction
    ? [
        {
          label: t('Account number', '계좌번호'),
          value: instruction.accountNumber,
          copyable: true,
        },
        { label: t('Bank', '은행'), value: instruction.bankName, copyable: false },
        {
          label: t('Account holder', '예금주'),
          value: instruction.accountHolder,
          copyable: false,
        },
      ]
    : [];

  // 상태별 머리말. 요청서의 확정 문구("Your booking is confirmed 🧘‍♀️")는 paid 에만 쓴다
  const header = {
    paid: {
      icon: 'check',
      title: t('Your booking is confirmed 🧘‍♀️', '예약이 확정되었습니다 🧘‍♀️'),
      body: t(
        'We have received your payment. See you at the workshop!',
        '입금이 확인되었습니다. 워크숍에서 만나요!',
      ),
    },
    awaiting: {
      icon: '🏦',
      title: t('Your spot is reserved', '자리가 예약되었습니다'),
      body: t(
        `Please transfer ${amountText} to the account below.`,
        `아래 계좌로 입금해 주세요. 입금 금액: ${amountText}`,
      ),
    },
    expired: {
      icon: '⏰',
      title: t('The transfer deadline has passed', '입금 기한이 지났습니다'),
      body: t(
        'We did not receive your transfer in time, so the spot was released. If you already sent the money, contact us and we will confirm your booking if a seat is still available.',
        '기한 내 입금이 확인되지 않아 자리가 해제되었습니다. 이미 송금하셨다면 문의해 주세요. 자리가 남아 있으면 예약을 확정해 드립니다.',
      ),
    },
    canceled: {
      icon: '🚫',
      title: t('This booking was canceled', '예약이 취소되었습니다'),
      body: t(
        'No transfer is needed. If you already sent the money, contact us and we will arrange a refund.',
        '입금하실 필요가 없습니다. 이미 송금하셨다면 문의해 주시면 환불해 드립니다.',
      ),
    },
    refund_requested: {
      icon: '↩️',
      title: t('Refund in progress', '환불 진행 중'),
      body: t(
        'Your refund request has been received. The organizer will send the refund to your bank account once it is reviewed.',
        '환불 요청이 접수되었습니다. 검토 후 주최측이 계좌로 환불해 드립니다.',
      ),
    },
    refunded: {
      icon: '✅',
      title: t('Refund completed', '환불 완료'),
      body: t(
        'Your refund has been sent to your bank account. It may take a few business days to appear.',
        '환불이 계좌로 송금되었습니다. 입금까지 며칠이 걸릴 수 있습니다.',
      ),
    },
  }[pageState];

  const primaryAction = isPaid
    ? { label: t('View my bookings', '내 예약 보기'), to: '/mypage?tab=Payments' }
    : isAwaiting
      ? {
          label: t('Done, I have sent the transfer', '입금을 완료했어요'),
          to: '/mypage?tab=Payments',
        }
      : pageState === 'refund_requested' || pageState === 'refunded'
        ? { label: t('View my payments', '결제 내역 보기'), to: '/mypage?tab=Payments' }
        : { label: t('Book again', '다시 예약하기'), to: eventDetailsPath };

  if (loading) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-grayscale-gray01'>
        <p className={`${applyTypography('h5')} text-text-primary`}>
          {t('Loading your transfer details...', '입금 안내를 불러오는 중...')}
        </p>
      </div>
    );
  }

  if (error || !instruction) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen bg-grayscale-gray01 gap-[16px] px-[16px]'>
        <p className={`${applyTypography('b16')} text-text-primary text-center`}>
          {error}
        </p>
        <BaseButton
          type='primary'
          size='m'
          hideIcon
          onClick={() => navigate('/mypage?tab=Payments')}
        >
          {t('Go to my payments', '결제 내역으로')}
        </BaseButton>
      </div>
    );
  }

  return (
    <>
      <div className='flex flex-col items-center relative w-full min-h-screen bg-grayscale-gray01 px-[16px] pt-0 pb-[80px] tabletH:px-[80px] tabletH:pt-[64px] tabletH:pb-[240px]'>
        {/* Mobile header */}
        <div className='tabletH:hidden bg-grayscale-gray01 flex items-center justify-between w-[calc(100%+32px)] -mx-[16px] px-[16px] pt-[16px] pb-[8px]'>
          <BaseButton
            type='text'
            size='s'
            onClick={() => navigate('/mypage?tab=Payments')}
            className='bg-grayscale-gray01 hover:bg-grayscale-gray01 h-auto min-w-0 border-0 p-[14px] rounded-[8px]'
            aria-label={t('Go back', '뒤로 가기')}
            icon={
              <div className='flex items-center overflow-clip p-[2px] size-[20px]'>
                <NavigationLeftIcon20 className='w-full h-full' />
              </div>
            }
          />
          <div className='flex flex-col items-center gap-[8px]'>
            <p className={`${applyTypography('title3')} text-text-primary`}>
              {t('Bank transfer', '계좌이체')}
            </p>
            <StepIndicator currentStep={3} className='scale-[0.92] origin-center' />
          </div>
          <div className='size-[48px] shrink-0' />
        </div>

        <div className='flex flex-col items-center w-full max-w-[570px] gap-[16px] tabletH:gap-[24px]'>
          {/* Status header */}
          <div className='flex flex-col items-center gap-[12px] pb-[8px] pt-[16px] tabletH:pt-0 w-full'>
            {header.icon === 'check' ? (
              <div className='bg-green-light02 rounded-full size-[90px] flex items-center justify-center'>
                <CheckCircleIcon className='size-[60px] text-icon-success' />
              </div>
            ) : (
              <div className='bg-grayscale-gray00 border border-grayscale-gray02 rounded-full size-[90px] flex items-center justify-center'>
                <span className='text-[36px]' aria-hidden='true'>
                  {header.icon}
                </span>
              </div>
            )}
            <p
              className={`hidden tabletH:block ${applyTypography('h7')} text-text-primary text-center`}
              style={{ lineHeight: '1.2' }}
            >
              {header.title}
            </p>
            <p
              className={`${applyTypography('b16')} text-text-secondary text-center whitespace-pre-wrap`}
              style={{ lineHeight: '1.5' }}
            >
              {header.body}
            </p>
          </div>

          {/* Bank transfer details — 요청서 문구 그대로. 입금 대기 중에만 계좌를 보여 준다
              (만료·취소 뒤에 계좌를 보이면 자리가 없는데 송금하는 일이 생긴다) */}
          {isAwaiting && (
            <div className='bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 rounded-[8px] p-[16px] tabletH:p-[40px] w-full flex flex-col gap-[16px]'>
              <p
                className={`${applyTypography('title3')} text-text-primary`}
                style={{ lineHeight: '1.3' }}
              >
                {t('Bank transfer details', '입금 계좌 안내')}
              </p>

              <ul className='flex flex-col gap-[10px]'>
                {detailRows.map((row) => (
                  <li
                    key={row.label}
                    className='flex items-center justify-between gap-[8px] bg-grayscale-gray01 rounded-[8px] p-[12px] tabletH:p-[16px]'
                  >
                    <p
                      className={`${applyTypography('b16')} text-text-primary break-all`}
                      style={{ lineHeight: '1.5' }}
                    >
                      <span className='text-text-secondary'>• {row.label}: </span>
                      <span className={applyTypography('b16Strong')}>
                        {row.value || '-'}
                      </span>
                    </p>
                    {row.copyable && row.value && (
                      <BaseButton
                        type='text'
                        size='s'
                        onClick={() => copy(row.value)}
                        className='min-w-0 rounded-none border-0 bg-transparent hover:bg-transparent p-[2px] size-[24px] shrink-0 hover:opacity-80 [&>span]:h-full'
                        aria-label={t(`Copy ${row.label}`, `${row.label} 복사`)}
                        icon={<CopyIcon className='w-full h-full text-icon-primary' />}
                      />
                    )}
                  </li>
                ))}
              </ul>

              {isAwaiting && (
                <p
                  className={`${applyTypography('b14')} text-text-primary`}
                  style={{ lineHeight: '1.6' }}
                >
                  {t(
                    'Please complete your payment within 3 hours of booking. Your booking will be canceled automatically if payment is not received in time.',
                    '예약 후 3시간 안에 입금을 완료해 주세요. 기한 내 입금이 확인되지 않으면 예약이 자동 취소됩니다.',
                  )}
                </p>
              )}

              <p
                className={`${applyTypography('b14')} text-text-primary`}
                style={{ lineHeight: '1.6' }}
              >
                {t('Please check the ', '취소 및 환불 정책은 ')}
                <a
                  href={eventDetailsPath}
                  className='underline text-text-primary'
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(eventDetailsPath);
                  }}
                >
                  {t('event details page', '이벤트 상세 페이지')}
                </a>
                {t(
                  ' for the cancellation and refund policy.',
                  '에서 확인해 주세요.',
                )}
              </p>

              {isAwaiting && (
                <div className='flex items-center justify-between border-t border-dashed border-grayscale-gray02 pt-[12px]'>
                  <div className='flex flex-col'>
                    <p className={`${applyTypography('b13')} text-text-secondary`}>
                      {t('Time left to pay', '남은 입금 시간')}
                    </p>
                    {deadlineText && (
                      <p className={`${applyTypography('b13')} text-text-placeholder`}>
                        {deadlineText} (KST)
                      </p>
                    )}
                  </div>
                  <p
                    className={`${applyTypography('b16Strong')} ${
                      secondsLeft <= 600
                        ? 'text-functional-failed03'
                        : 'text-text-primary'
                    }`}
                  >
                    {formatCountdown(secondsLeft)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Amount, transfer name, booking summary */}
          <div className='bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 rounded-[8px] p-[16px] tabletH:p-[40px] w-full flex flex-col gap-[16px]'>
            {(isAwaiting || isPaid) && (
              <div
                className={`flex items-baseline justify-between ${
                  isAwaiting
                    ? ''
                    : 'border-b-[1.6px] border-dashed border-grayscale-gray02 pb-[16px]'
                }`}
              >
                <p className={`${applyTypography('b14Strong')} text-text-primary`}>
                  {isPaid ? t('Amount paid', '결제 금액') : t('Amount', '입금 금액')}
                </p>
                <p className={`${applyTypography('b18Strong')} text-green-deep04`}>
                  {amountText}
                </p>
              </div>
            )}
            {isAwaiting && (
              <>
                <div className='flex flex-col gap-[4px] border-b-[1.6px] border-dashed border-grayscale-gray02 pb-[16px]'>
                  <p className={`${applyTypography('b13')} text-text-secondary`}>
                    {t(
                      'Transfer name · use your Koready ID',
                      '이체명 · 내 코레디 아이디를 적어 주세요',
                    )}
                  </p>
                  <div className='bg-grayscale-gray01 flex items-center justify-between p-[12px] rounded-[8px] gap-[8px]'>
                    <p
                      className={`flex-1 ${applyTypography('b14Strong')} text-text-primary break-all`}
                    >
                      {instruction.transferName || '-'}
                    </p>
                    {instruction.transferName && (
                      <BaseButton
                        type='text'
                        size='s'
                        onClick={() => copy(instruction.transferName)}
                        className='min-w-0 rounded-none border-0 bg-transparent hover:bg-transparent p-[2px] size-[20px] shrink-0 hover:opacity-80 [&>span]:h-full'
                        aria-label={t('Copy transfer name', '이체명 복사')}
                        icon={<CopyIcon className='w-full h-full text-icon-primary' />}
                      />
                    )}
                  </div>
                </div>
              </>
            )}

            <p className={`${applyTypography('b14Strong')} text-text-primary`}>
              {t('Your booking', '예약 내역')}
            </p>
            {(instruction.items || []).map((item) => (
              <div
                key={item.bookingId}
                className='flex flex-col gap-[6px] border-b-[1.6px] border-dashed border-grayscale-gray02 last:border-b-0 pb-[12px] last:pb-0'
              >
                <div className='flex items-start justify-between gap-[8px]'>
                  <p className={`${applyTypography('b16Strong')} text-text-primary`}>
                    {item.workshopTitle}
                  </p>
                  {itemStatusLabel(item.bookingStatus) && (
                    <span
                      className={`${applyTypography('b12Strong')} text-text-secondary whitespace-nowrap`}
                    >
                      {itemStatusLabel(item.bookingStatus)}
                    </span>
                  )}
                </div>
                <p className={`${applyTypography('b14')} text-text-secondary`}>
                  {item.date ? formatDate(item.date) : ''}
                  {item.startTime && item.endTime
                    ? ` · ${formatTime(item.startTime)} - ${formatTime(item.endTime)}`
                    : ''}
                </p>
                {(item.meetingPointName || item.meetingPointAddress) && (
                  <p className={`${applyTypography('b13')} text-text-secondary`}>
                    {[item.meetingPointName, item.meetingPointAddress]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                <div className='flex items-center justify-between gap-[8px] bg-grayscale-gray01 rounded-[8px] p-[12px]'>
                  <p className={`${applyTypography('b14')} text-text-secondary`}>
                    {t('Booking number', '예약 번호')}
                  </p>
                  <div className='flex items-center gap-[6px]'>
                    <p className={`${applyTypography('b14Strong')} text-text-primary`}>
                      {item.bookingSerialNumber || '-'}
                    </p>
                    <BaseButton
                      type='text'
                      size='s'
                      onClick={() => copy(item.bookingSerialNumber)}
                      isDisabled={!item.bookingSerialNumber}
                      className='min-w-0 rounded-none border-0 bg-transparent hover:bg-transparent p-[2px] size-[20px] hover:opacity-80 [&>span]:h-full'
                      aria-label={t('Copy booking number', '예약 번호 복사')}
                      icon={<CopyIcon className='w-full h-full text-icon-primary' />}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className='flex flex-col gap-[8px] w-full'>
            <BaseButton
              type='primary'
              size='l'
              hideIcon
              width='100%'
              onClick={() => navigate(primaryAction.to)}
            >
              {primaryAction.label}
            </BaseButton>
            <p
              className={`${applyTypography('b14')} text-text-secondary text-center pt-[8px]`}
              style={{ lineHeight: '1.6' }}
            >
              💌 <strong>{t('Questions:', '문의:')}</strong>{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className='underline text-text-primary'>
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </div>

      {copyToast}
    </>
  );
};

export default BankTransfer;
